/**
 * 窗口尺寸监听工具类
 * 用于获取当前窗口宽度，并动态监听窗口大小变化
 * 提供响应式断点判断（isMobile_900, isMobile_810）
 *
 * 使用方式：
 *
 * // 方式 1：订阅窗口尺寸变化（任何尺寸变化都会触发）
 * const unsubscribe = window.windowResizeMonitor.subscribe((data) => {
 *   console.log('窗口宽度:', data.innerWidth);
 *   console.log('是否移动端 ≤900px:', data.isMobile_900);
 *   console.log('是否移动端 ≤810px:', data.isMobile_810);
 * });
 *
 * // 方式 2：监听断点变化事件（只在断点改变时触发，更高效）
 * window.addEventListener('breakpoint:change', (e) => {
 *   console.log('断点变化:', e.detail);
 *   console.log('从哪个断点变化:', e.detail.changedFrom);
 * });
 *
 * // 方式 3：直接获取当前值
 * const width = window.windowResizeMonitor.getInnerWidth();
 * const isMobile810 = window.windowResizeMonitor.getIsMobile_810();
 *
 * 性能优势：
 * - 全局唯一的 resize 监听器（避免重复监听）
 * - 自动节流（100ms）+ requestAnimationFrame 优化
 * - 只在值改变时才触发回调
 * - breakpoint:change 事件只在断点变化时触发（性能最优）
 */
class WindowResizeMonitor {
  constructor() {
    // 当前窗口宽度
    this.innerWidth = window.innerWidth;
    // 是否小于等于 900px
    this.isMobile_900 = window.innerWidth <= 900;
    // 是否小于等于 810px
    this.isMobile_810 = window.innerWidth <= 810;
    // 是否大于等于 1980px
    this.isWide_1980 = window.innerWidth >= 1980;
    // 节流定时器
    this.resizeTimer = null;
    // 回调函数列表
    this.callbacks = [];
    // 是否已经初始化
    this.initialized = false;
  }

  /**
   * 初始化监听器
   */
  init() {
    if (this.initialized) {
      return this;
    }

    this.initialized = true;

    // 使用 resize 事件监听窗口变化，使用节流优化性能
    window.addEventListener('resize', () => {
      // 清除之前的定时器
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }

      // 使用 requestAnimationFrame 优化性能
      this.resizeTimer = setTimeout(() => {
        if (window.requestAnimationFrame) {
          requestAnimationFrame(() => {
            this.updateValues();
          });
        } else {
          this.updateValues();
        }
      }, 100); // 100ms 节流
    });

    // 初始化时更新一次
    this.updateValues();

    return this;
  }

  /**
   * 更新窗口尺寸相关的值
   */
  updateValues() {
    const oldWidth = this.innerWidth;
    const oldIsMobile_900 = this.isMobile_900;
    const oldIsMobile_810 = this.isMobile_810;
    const oldIsWide_1980 = this.isWide_1980;

    // 更新当前窗口宽度
    this.innerWidth = window.innerWidth;
    // 更新断点判断
    this.isMobile_900 = window.innerWidth <= 900;
    this.isMobile_810 = window.innerWidth <= 810;
    this.isWide_1980 = window.innerWidth >= 1980;

    // 检查断点是否变化
    const breakpointChanged =
      oldIsMobile_900 !== this.isMobile_900 ||
      oldIsMobile_810 !== this.isMobile_810 ||
      oldIsWide_1980 !== this.isWide_1980;

    // 如果断点发生变化，触发自定义事件
    if (breakpointChanged) {
      window.dispatchEvent(new CustomEvent('breakpoint:change', {
        detail: {
          innerWidth: this.innerWidth,
          isMobile_900: this.isMobile_900,
          isMobile_810: this.isMobile_810,
          isWide_1980: this.isWide_1980,
          // 提供断点变化详情
          changedFrom: {
            isMobile_900: oldIsMobile_900,
            isMobile_810: oldIsMobile_810,
            isWide_1980: oldIsWide_1980
          }
        }
      }));
    }

    // 如果任何值发生变化，触发回调
    if (
      oldWidth !== this.innerWidth ||
      oldIsMobile_900 !== this.isMobile_900 ||
      oldIsMobile_810 !== this.isMobile_810 ||
      oldIsWide_1980 !== this.isWide_1980
    ) {
      this.notifyCallbacks();
    }
  }

  /**
   * 通知所有回调函数
   */
  notifyCallbacks() {
    this.callbacks.forEach(callback => {
      try {
        callback({
          innerWidth: this.innerWidth,
          isMobile_900: this.isMobile_900,
          isMobile_810: this.isMobile_810,
          isWide_1980: this.isWide_1980
        });
      } catch (error) {
        console.error('WindowResizeMonitor 回调执行错误:', error);
      }
    });
  }

  /**
   * 添加窗口尺寸变化的回调函数
   * @param {Function} callback - 回调函数，会接收 { innerWidth, isMobile_900, isMobile_810 } 作为参数
   * @returns {Function} 返回一个取消订阅的函数
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      console.error('WindowResizeMonitor.subscribe: 回调必须是一个函数');
      return () => {};
    }

    this.callbacks.push(callback);

    // 立即执行一次回调，传递当前值
    callback({
      innerWidth: this.innerWidth,
      isMobile_900: this.isMobile_900,
      isMobile_810: this.isMobile_810,
      isWide_1980: this.isWide_1980
    });

    // 返回取消订阅函数
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  /**
   * 获取当前窗口宽度
   * @returns {number}
   */
  getInnerWidth() {
    return this.innerWidth;
  }

  /**
   * 判断是否为移动端（<= 900px）
   * @returns {boolean}
   */
  getIsMobile_900() {
    return this.isMobile_900;
  }

  /**
   * 判断是否为移动端（<= 810px）
   * @returns {boolean}
   */
  getIsMobile_810() {
    return this.isMobile_810;
  }

  /**
   * 判断是否为大屏（>= 1980px）
   * @returns {boolean}
   */
  getIsWide_1980() {
    return this.isWide_1980;
  }

  /**
   * 销毁监听器
   */
  destroy() {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.callbacks = [];
    this.initialized = false;
  }
}

// 创建全局单例实例
if (!window.windowResizeMonitor) {
  window.windowResizeMonitor = new WindowResizeMonitor().init();
}
